import React from 'react';
import {
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  PencilIcon,
  TrashIcon,
  ArrowRightIcon,
  ClockIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  StarIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';
import { Lead, LeadStatus } from '../../types/lead';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';

interface LeadCardProps {
  lead: Lead;
  onEdit: (lead: Lead) => void;
  onDelete: (leadId: string) => void;
  onConvert: (lead: Lead) => void;
  onStatusChange: (leadId: string, status: LeadStatus) => void;
  onViewComments: (lead: Lead) => void;
}

const LeadCard: React.FC<LeadCardProps> = ({
  lead,
  onEdit,
  onDelete,
  onConvert,
  onStatusChange,
  onViewComments,
}) => {
  const getStatusColor = (status: LeadStatus) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'contacted':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'qualified':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'unqualified':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'converted':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const getStatusLabel = (status: LeadStatus) => {
    switch (status) {
      case 'new':
        return 'Új';
      case 'contacted':
        return 'Kapcsolatba lépve';
      case 'qualified':
        return 'Minősített';
      case 'unqualified':
        return 'Nem minősített';
      case 'converted':
        return 'Konvertálva';
      default:
        return status;
    }
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'google_sheets':
        return 'Google Sheets';
      case 'manual':
        return 'Manuális';
      case 'website':
        return 'Weboldal';
      case 'referral':
        return 'Ajánlás';
      default:
        return source;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              {lead.name}
            </h3>
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                {getStatusLabel(lead.status)}
              </span>
              {lead.score !== undefined && (
                <div className="flex items-center space-x-1">
                  <StarIcon className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">{lead.score}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => onViewComments(lead)}
              className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="Megjegyzések"
            >
              <ChatBubbleLeftIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => onEdit(lead)}
              className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              title="Szerkesztés"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            {lead.status !== 'converted' && (
              <button
                onClick={() => onDelete(lead.id)}
                className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                title="Törlés"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {lead.company && (
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <BuildingOfficeIcon className="h-4 w-4 mr-2 flex-shrink-0" />
            <span className="truncate">{lead.company}</span>
          </div>
        )}
        
        {lead.email && (
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <EnvelopeIcon className="h-4 w-4 mr-2 flex-shrink-0" />
            <a 
              href={`mailto:${lead.email}`}
              className="truncate hover:text-primary-600 dark:hover:text-primary-400"
            >
              {lead.email}
            </a>
          </div>
        )}
        
        {lead.phone && (
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <PhoneIcon className="h-4 w-4 mr-2 flex-shrink-0" />
            <a 
              href={`tel:${lead.phone}`}
              className="truncate hover:text-primary-600 dark:hover:text-primary-400"
            >
              {lead.phone}
            </a>
          </div>
        )}

        {lead.notes && (
          <div className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
            {lead.notes}
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-500 pt-2 border-t border-gray-100 dark:border-gray-700">
          <span>Forrás: {getSourceLabel(lead.source)}</span>
          <span>{format(lead.createdAt, 'yyyy.MM.dd', { locale: hu })}</span>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
        {lead.status === 'converted' ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-purple-600 dark:text-purple-400">
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              Konvertálva ügyféllé
            </div>
            <button
              onClick={() => onStatusChange(lead.id, 'qualified')}
              className="px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              title="Visszaállítás minősített státuszra"
            >
              Visszaállítás
            </button>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <select
              value={lead.status}
              onChange={(e) => onStatusChange(lead.id, e.target.value as LeadStatus)}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="new">Új</option>
              <option value="contacted">Kapcsolatba lépve</option>
              <option value="qualified">Minősített</option>
              <option value="unqualified">Nem minősített</option>
            </select>
            <button
              onClick={() => onConvert(lead)}
              className="inline-flex items-center px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-colors"
              title="Konvertálás ügyféllé"
            >
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadCard;
